import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { withPathPrefix } from '../../pathPrefix';

interface PromoBannerProps {
  /** Heading element. Website catalog passes 'h1'; miniapp uses the default 'h2'. */
  headingLevel?: 'h1' | 'h2';
  className?: string;
}

const features = ['handPicked', 'handProcessed', 'organic'] as const;

// Soft oval of the light-theme background (parchment) behind each text block, so the photo stays untouched elsewhere.
// Glows share the section stacking context (-z-10, above the -z-20 photo) so no glow covers other text.
const textGlowClass =
  "relative before:pointer-events-none before:absolute before:-inset-x-10 before:-inset-y-6 before:-z-10 before:rounded-full before:bg-parchment before:opacity-90 before:blur-xl before:content-['']";

const desktopImageUrl = withPathPrefix('/assets/branding/banner_desktop.webp');
const mobileImageUrl = withPathPrefix('/assets/branding/banner_mobile.webp');

function LeafIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-6 w-6 text-olive md:h-7 md:w-7"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      viewBox="0 0 24 24"
    >
      <path d="M12 21v-8" />
      <path d="M12 13c0-4.5 3-7.5 8-8-.5 5-3.5 8-8 8Z" />
      <path d="M12 15c-.5-3.5-3-6-7-6.5.3 3.8 2.8 6.3 7 6.5Z" />
    </svg>
  );
}

export default function PromoBanner({ headingLevel = 'h2', className = '' }: PromoBannerProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const Heading = headingLevel;

  return (
    <section
      aria-labelledby={headingId}
      className={`relative isolate overflow-hidden bg-parchment ${className}`.trim()}
    >
      <picture aria-hidden="true">
        <source media="(min-width: 768px)" srcSet={desktopImageUrl} />
        <img
          src={mobileImageUrl}
          alt=""
          loading="eager"
          decoding="async"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-bottom md:object-center"
        />
      </picture>
      <div className="relative flex min-h-[26rem] flex-col justify-start gap-4 px-5 pb-40 pt-6 md:min-h-[24rem] md:max-w-[70%] md:justify-center md:gap-5 md:px-12 md:py-12 lg:min-h-[28rem] lg:px-16">
        <Heading
          id={headingId}
          className={`m-0 max-w-[20ch] text-[1.75rem] font-semibold leading-[1.1] tracking-[-0.02em] w-fit text-ink [text-wrap:balance] md:max-w-none md:text-5xl lg:text-[3.25rem] ${textGlowClass}`}
        >
          {t('promoBanner.title')}
        </Heading>
        <p className={`m-0 max-w-[42ch] text-sm w-fit leading-relaxed text-ink md:text-lg lg:text-xl ${textGlowClass}`}>
          {t('promoBanner.subtitle')}
        </p>
        <ul
          aria-label={t('promoBanner.featuresAria')}
          className={`m-0 mt-1 grid list-none grid-cols-3 p-0 md:mt-3 md:flex md:max-w-max ${textGlowClass}`}
        >
          {features.map((key) => (
            <li
              key={key}
              className="flex flex-col items-center gap-1.5 px-2 text-center text-xs font-medium leading-snug text-ink [&:not(:first-child)]:border-l [&:not(:first-child)]:border-sand md:px-6 md:text-sm md:first:pl-0"
            >
              <LeafIcon />
              <span>{t(`promoBanner.features.${key}`)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
